/**
 * HCaptchaModal – On-Demand-hCaptcha-Challenge.
 *
 * Wird von den Notify-Formularen geöffnet, sobald das Backend
 * `captcha_required: true` meldet (weiches Rate-Limit-Fallback). Sobald der
 * Nutzer das Widget löst, feuern wir `onSolve(token)` — das Formular
 * wiederholt dann den POST mit `hcaptcha_token`.
 *
 * Wir laden hCaptcha lazy nur bei tatsächlicher Anzeige (spart Payload bei
 * legitimen Nutzern).
 */
import React, { Suspense, lazy } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const HCaptcha = lazy(() => import('@hcaptcha/react-hcaptcha'));

const SITE_KEY =
  process.env.REACT_APP_HCAPTCHA_SITE_KEY ||
  // Öffentlicher Sandbox-Test-Key (löst immer als „gültig" ohne Puzzle)
  '10000000-ffff-ffff-ffff-000000000001';

export default function HCaptchaModal({ open, onSolve, onClose, message }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[400]"
            data-testid="hcaptcha-modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[401] w-[92vw] max-w-[420px] bg-white rounded-2xl shadow-2xl p-6"
            data-testid="hcaptcha-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500"
              aria-label="Schließen"
              data-testid="hcaptcha-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="hs-serif text-lg text-[#171512] leading-tight">Kurze Sicherheits­prüfung</h3>
                <p className="text-[11px] text-[#8a6d10]">Nur einmal — dann wieder normal weiter.</p>
              </div>
            </div>
            <p className="text-[13px] text-[#3a2f1e]/80 mb-4 leading-relaxed">
              {message || 'Wir sehen ungewöhnlich viele Anfragen aus deinem Netzwerk. Bestätige kurz, dass du kein Bot bist.'}
            </p>
            <div className="flex justify-center min-h-[80px]" data-testid="hcaptcha-widget-slot">
              <Suspense fallback={<div className="text-xs text-stone-500">Lade Sicherheits-Widget…</div>}>
                <HCaptcha
                  sitekey={SITE_KEY}
                  theme="light"
                  onVerify={(token) => onSolve?.(token)}
                  onError={() => onClose?.()}
                  onExpire={() => onClose?.()}
                />
              </Suspense>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
